/**
 * Convertit la première page d'un PDF en image JPEG.
 * Charge pdfjs-dist v5.5 (legacy build) depuis public/ via un <script>
 * pour contourner l'incompatibilité Turbopack.
 * Le legacy build garantit la compatibilité avec tous les navigateurs.
 * Les fichiers dans public/ sont servis par le service worker → offline OK.
 */

interface PdfToImageOptions {
  /** Largeur max en pixels (défaut : 3000) */
  maxWidth?: number;
  /** Qualité JPEG 0-1 (défaut : 0.85) */
  quality?: number;
}

// Cache : ne charger le script qu'une seule fois
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let pdfjsLibPromise: Promise<any> | null = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function loadPdfjs(): Promise<any> {
  if (pdfjsLibPromise) return pdfjsLibPromise;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const existing = (window as any).__pdfjsLib;
  if (existing) {
    pdfjsLibPromise = Promise.resolve(existing);
    return pdfjsLibPromise;
  }

  pdfjsLibPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.type = "module";
    // Loader externalisé (public/pdf-loader.js) plutôt qu'inline : permet une CSP
    // `script-src 'self'` stricte sans `unsafe-inline`. Cf. public/pdf-loader.js.
    script.src = "/pdf-loader.js";

    const onReady = () => {
      window.removeEventListener("pdfjs-ready", onReady);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const lib = (window as any).__pdfjsLib;
      if (lib) {
        resolve(lib);
      } else {
        pdfjsLibPromise = null;
        reject(new Error("pdfjs-dist n'a pas pu être chargé"));
      }
    };

    window.addEventListener("pdfjs-ready", onReady);

    script.onerror = () => {
      window.removeEventListener("pdfjs-ready", onReady);
      pdfjsLibPromise = null;
      reject(new Error("Impossible de charger la librairie PDF"));
    };

    // Timeout de sécurité (10s)
    setTimeout(() => {
      window.removeEventListener("pdfjs-ready", onReady);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (!(window as any).__pdfjsLib) {
        pdfjsLibPromise = null;
        reject(new Error("Timeout : chargement de la librairie PDF trop long"));
      }
    }, 10000);

    document.head.appendChild(script);
  });

  return pdfjsLibPromise;
}

/**
 * Convertit un Blob PDF en Blob JPEG (première page uniquement).
 */
export async function pdfToImage(
  pdfBlob: Blob,
  options?: PdfToImageOptions
): Promise<Blob> {
  const { maxWidth = 3000, quality = 0.85 } = options ?? {};

  const pdfjsLib = await loadPdfjs();

  const arrayBuffer = await pdfBlob.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(1);

  // Calculer le scale pour atteindre maxWidth
  const viewport = page.getViewport({ scale: 1 });
  const scale = Math.min(maxWidth / viewport.width, maxWidth / viewport.height);
  const scaledViewport = page.getViewport({ scale });

  // Rendre sur un canvas
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(scaledViewport.width);
  canvas.height = Math.round(scaledViewport.height);

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Impossible de créer le contexte canvas");
  }

  // Fond blanc (les PDF peuvent avoir un fond transparent)
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  await page.render({
    canvasContext: ctx,
    viewport: scaledViewport,
  }).promise;

  // Exporter en JPEG
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Échec de la conversion PDF en image"));
        }
      },
      "image/jpeg",
      quality
    );
  });
}
